import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { ArrowLeft, MapPin, Phone, Store, Plus, ShoppingCart } from "lucide-react";
import { grocery, addToCart, cartTotals, formatXAF } from "../../lib/grocery";
import { toast } from "sonner";

export default function ShopPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("");
  const [cart, setCart] = useState(cartTotals());

  useEffect(() => {
    grocery.shop(id).then(setShop).catch(() => setShop(null));
    grocery.products({ shop_id: id, limit: 200 }).then(setProducts);
    grocery.categories().then((d) => setCategories(d.categories || []));
  }, [id]);

  useEffect(() => {
    const refresh = () => setCart(cartTotals());
    window.addEventListener("kanama-cart-changed", refresh);
    return () => window.removeEventListener("kanama-cart-changed", refresh);
  }, []);

  const handleAdd = (p) => {
    addToCart(p, 1);
    toast.success(`${p.name} ajouté au panier`);
  };

  const cats = Array.from(new Set(products.map((p) => p.category)));
  const visible = activeCat ? products.filter((p) => p.category === activeCat) : products;

  if (!shop) {
    return (
      <div className="max-w-4xl mx-auto px-5 py-10" data-testid="grocery-shop-loading">
        <Link to="/courses" className="text-brand-inkSoft text-sm flex items-center gap-1 mb-4">
          <ArrowLeft className="w-4 h-4" /> Retour
        </Link>
        <p className="text-brand-inkSoft">Chargement de la boutique…</p>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] bg-brand-paper pb-24" data-testid="grocery-shop-page">
      {/* Header */}
      <section className="bg-brand-ink text-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-6">
          <Link to="/courses" className="text-white/70 text-sm flex items-center gap-1 mb-3 hover:text-brand-gold" data-testid="grocery-back-home">
            <ArrowLeft className="w-4 h-4" /> Toutes les boutiques
          </Link>
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-brand-gold/20 text-brand-gold flex items-center justify-center shrink-0">
              <Store className="w-7 h-7" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-2xl sm:text-3xl font-semibold">{shop.name}</h1>
              <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-white/80">
                <span className="inline-flex items-center gap-1">
                  <MapPin className="w-4 h-4" /> {shop.address}
                </span>
                {shop.phone && (
                  <span className="inline-flex items-center gap-1">
                    <Phone className="w-4 h-4" /> {shop.phone}
                  </span>
                )}
                {shop.open && <Badge className="bg-emerald-600 hover:bg-emerald-700">Ouvert</Badge>}
              </div>
              {shop.description && <p className="mt-2 text-white/80 text-sm">{shop.description}</p>}
            </div>
          </div>
        </div>
      </section>

      {/* Category tabs */}
      <div className="sticky top-[60px] z-20 bg-brand-paper/95 backdrop-blur-md border-b border-brand-line">
        <div className="max-w-5xl mx-auto px-3 sm:px-6 py-3 overflow-x-auto">
          <div className="flex gap-2 min-w-max">
            <button
              onClick={() => setActiveCat("")}
              data-testid="grocery-shop-filter-all"
              className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${
                !activeCat ? "bg-brand-ink text-brand-gold" : "bg-white text-brand-inkSoft border border-brand-line"
              }`}
            >
              Tout ({products.length})
            </button>
            {cats.map((key) => {
              const c = categories.find((cc) => cc.key === key);
              if (!c) return null;
              const active = activeCat === key;
              const count = products.filter((p) => p.category === key).length;
              return (
                <button
                  key={key}
                  onClick={() => setActiveCat(key)}
                  data-testid={`grocery-shop-filter-${key}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap inline-flex items-center gap-1 ${
                    active ? "bg-brand-ink text-brand-gold" : "bg-white text-brand-inkSoft border border-brand-line"
                  }`}
                >
                  <span>{c.icon}</span>
                  {c.label} ({count})
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Products grid */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {visible.length === 0 ? (
          <p className="text-brand-inkSoft">Aucun produit disponible.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {visible.map((p) => (
              <Card key={p.id} className="p-3 rounded-2xl border-brand-line flex flex-col" data-testid={`grocery-shop-product-${p.id}`}>
                <div className="aspect-square rounded-xl bg-brand-sandlight flex items-center justify-center mb-2 overflow-hidden">
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-4xl opacity-70">
                      {categories.find((c) => c.key === p.category)?.icon || "🛒"}
                    </span>
                  )}
                </div>
                <h3 className="font-semibold text-brand-ink text-sm leading-tight line-clamp-2 mb-1">{p.name}</h3>
                <div className="text-[11px] text-brand-inkSoft uppercase tracking-wider mb-2">
                  {p.subtype ? `${p.subtype} · ` : ""}par {p.unit}
                </div>
                <div className="flex items-center justify-between mt-auto gap-2">
                  <span className="font-heading font-bold text-brand-goldDark whitespace-nowrap">
                    {formatXAF(p.price_xaf)}
                  </span>
                  <Button
                    size="sm"
                    onClick={() => handleAdd(p)}
                    className="h-8 px-2 rounded-lg bg-brand-ink hover:bg-brand-inkSoft text-white"
                    data-testid={`grocery-shop-add-${p.id}`}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* Floating cart */}
      {cart.count > 0 && (
        <div
          className="fixed left-3 right-3 z-40 max-w-md mx-auto"
          style={{ bottom: "calc(72px + env(safe-area-inset-bottom, 0))" }}
        >
          <button
            onClick={() => navigate("/courses/panier")}
            className="w-full bg-brand-ink text-white rounded-2xl px-4 py-3 flex items-center justify-between shadow-2xl active:scale-[0.98] transition-transform"
            data-testid="grocery-shop-floating-cart"
          >
            <span className="flex items-center gap-2">
              <span className="bg-brand-gold text-brand-black rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold">
                {cart.count}
              </span>
              <span className="font-semibold inline-flex items-center gap-2">
                <ShoppingCart className="w-4 h-4" /> Voir mon panier
              </span>
            </span>
            <span className="text-brand-gold font-bold">{formatXAF(cart.subtotal_xaf)}</span>
          </button>
        </div>
      )}
    </div>
  );
}
